import React, { useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import {
  CBadge,
  CButton,
  CCard,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilPeople,
  cilWarning,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  ImportFileBar,
  ImportUploadStep,
  InfoBox,
  MiniStepper,
  StatTile,
  tintStyle,
} from 'src/components/common/ImportWizard'
import {
  MATCH_META,
  buildPlayerIndex,
  readPlayerCells,
  resolvePlayer,
  takePlayerColumns,
} from 'src/components/common/playerMatching'
import { POSITION_META } from '../ScoreCard/scorecardConfig'
import {
  RowScores,
  parseStatCells,
  readStatCells,
  takeStatColumns,
} from '../ScoreCard/scorecardCsv'

const WIZARD_STEPS = ['Upload File', 'Validate Players', 'Review & Import']

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  unmatched_player: {
    label: 'Player Not Found',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  ambiguous_player: {
    label: 'Several Players Match',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  duplicate: { label: 'Duplicate Row', color: 'warning', icon: cilWarning, accent: '#b45309' },
  nothing_to_do: {
    label: 'Already In Squad',
    color: 'info',
    icon: cilCheckCircle,
    accent: '#087990',
  },
}

const playerLabel = (player) => {
  const position = POSITION_META[player?.position]
  const suffix = [position?.label, player?.team_name].filter(Boolean).join(' · ')
  return suffix ? `${player.full_name} (${suffix})` : player?.full_name || ''
}

// A plain list of names builds a squad and nothing else. A match export carries the innings'
// figures in the same file, so any column that names a stat this game scores is read too and
// those figures are saved onto the scorecard in the same pass. Everything else is ignored, which
// is what lets a player export, a match export or a hand-written list all be dropped in
// unchanged.
const readSheet = (json, rawHeaders, scorecardColumns) => {
  const pool = [...rawHeaders]
  const playerColumns = takePlayerColumns(pool)
  if (!playerColumns.found) {
    return { error: 'This file has no player name or player ID column.' }
  }

  const statColumns = takeStatColumns(pool, json, scorecardColumns)

  const baseRows = json
    .map((r) => ({
      ...readPlayerCells(r, playerColumns),
      cells: readStatCells(r, statColumns),
      manual_id: '',
    }))
    // Rows with no player on them are the blank lines spreadsheets leave behind, not data.
    .filter((r) => r.csv_name !== '' || r.csv_player_id !== '')

  if (!baseRows.length) return { error: 'This file has no rows to import.' }

  return { baseRows, statColumns, ignoredHeaders: pool }
}

// Re-run over the whole file after every mapping change - which player a row points at decides
// whether some other row is now a duplicate, so a single row cannot be re-checked on its own.
const buildValidatedRows = (baseRows, statColumns, index, squadIds) => {
  const claimed = new Set()

  return baseRows.map((base, i) => {
    const { record: player, match, ambiguous } = resolvePlayer(base, index)
    const { values, invalidCells } = parseStatCells(base.cells, statColumns)
    const hasValues = Object.keys(values).length > 0
    const inSquad = Boolean(player && squadIds.has(String(player.id)))

    // Being in the squad already is not a reason to skip a row when the file also carries that
    // player's figures - there is still a scorecard to write. It is only a dead row when the
    // file has nothing but the name.
    let result = 'valid'
    if (!player) {
      result = ambiguous ? 'ambiguous_player' : 'unmatched_player'
    } else if (claimed.has(String(player.id))) {
      result = 'duplicate'
    } else if (inSquad && !hasValues) {
      result = 'nothing_to_do'
    }

    if (result === 'valid') claimed.add(String(player.id))

    return {
      ...base,
      row: i + 1,
      player_key: player ? String(player.id) : '',
      player_name: player?.full_name || '',
      player_position: player?.position,
      team_name: player?.team_name || '',
      match,
      ambiguous_count: ambiguous?.length || 0,
      in_squad: inSquad,
      values,
      invalid_cells: invalidCells,
      result,
      included: result === 'valid',
    }
  })
}

// Squad Import: build this fixture's squad from a spreadsheet of names, mapping every row onto
// a player in the club's pool - by name where the file and the pool agree, by hand where they
// do not. Same 3-step shape as the other bulk imports; see the README.
const SquadCsvImport = ({
  players,
  squad,
  scorecardColumns,
  columnsLoading = false,
  innings,
  inningsOptions,
  onInningsChange,
  onImport,
  onCancel,
}) => {
  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)

  const [rows, setRows] = useState([])
  const [statColumns, setStatColumns] = useState([])
  const [ignoredHeaders, setIgnoredHeaders] = useState([])

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  const playerPool = useMemo(() => players || [], [players])

  const squadIds = useMemo(
    () => new Set((squad || []).map((picked) => String(picked.player_id))),
    [squad],
  )

  const playerIndex = useMemo(
    () =>
      buildPlayerIndex(playerPool, {
        getKey: (player) => player.id,
        getPlayerId: (player) => player.id,
        getName: (player) => player.full_name,
      }),
    [playerPool],
  )

  const playerOptions = useMemo(
    () =>
      playerPool
        .map((player) => ({ value: String(player.id), label: playerLabel(player) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [playerPool],
  )

  const inningsLabel =
    inningsOptions?.length > 1
      ? inningsOptions.find((o) => o.value === innings)?.label || 'this innings'
      : 'this innings'

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
    setStatColumns([])
    setIgnoredHeaders([])
    setImportErrors([])
  }

  // Built from the pool already on screen rather than fetched: the admin deletes the rows for
  // players they are not picking and uploads what is left.
  const handleDownloadTemplate = () => {
    const header = ['Player', 'Position', 'Team', 'Player ID']
    const body = playerPool.map((player) => [
      player.full_name,
      POSITION_META[player.position]?.label ?? '',
      player.team_name ?? '',
      player.id,
    ])
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body])
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, 'Squad')
    XLSX.writeFile(book, 'MyClubtap-SquadImport.csv', { bookType: 'csv' })
  }

  const handleFile = (file) => {
    if (!file) return
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const read = readSheet(json, rawHeaders, scorecardColumns)
      if (read.error) {
        ToastComponent(read.error, 'error')
        setBusy(false)
        return
      }

      setFileName(file.name)
      setFileRowCount(read.baseRows.length)
      setStatColumns(read.statColumns)
      setIgnoredHeaders(read.ignoredHeaders)
      setRows(buildValidatedRows(read.baseRows, read.statColumns, playerIndex, squadIds))
      setImportErrors([])
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const remapRow = (row, playerKey) => {
    setRows((prev) => {
      const baseRows = prev.map((r) => (r.row === row ? { ...r, manual_id: playerKey } : r))
      const revalidated = buildValidatedRows(baseRows, statColumns, playerIndex, squadIds)
      // A row the admin has just pointed somewhere comes back included; every other row keeps
      // the include/exclude decision already made for it.
      return revalidated.map((r, idx) => ({
        ...r,
        included: r.result === 'valid' && (r.row === row || prev[idx].included),
      }))
    })
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    const alreadyIn = rows.filter((r) => r.result === 'nothing_to_do').length
    return { valid, alreadyIn, needsAttention: rows.length - valid - alreadyIn }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)
  const hasStats = statColumns.length > 0
  // Only players not already picked need adding; the rest of an included row is its scores.
  const newPlayerRows = readyRows.filter((r) => !r.in_squad)
  const scoreRows = readyRows.filter((r) => Object.keys(r.values).length > 0)
  const skippedCells = rows.reduce((total, r) => total + r.invalid_cells, 0)

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const res = await onImport(
        newPlayerRows.map((r) => r.player_key),
        scoreRows.map((r) => ({ playerId: r.player_key, values: r.values })),
      )
      if (res?.success) {
        resetAll()
      } else {
        setImportErrors(
          Array.isArray(res?.errors) && res.errors.length
            ? res.errors
            : ['Something went wrong. Please try again.'],
        )
      }
    } catch (e) {
      setImportErrors(['Something went wrong. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  const renderUpload = () => (
    <>
      <ImportUploadStep
        templateHint="The template lists every player you can pick, with their player ID already filled in."
        templateButton={
          <CButton
            type="button"
            color="primary"
            variant="outline"
            disabled={!playerPool.length}
            onClick={handleDownloadTemplate}
          >
            <CIcon icon={cilCloudDownload} className="me-1" />
            Download Squad Template
          </CButton>
        }
        requiredColumns={[
          <>
            Player <span className="text-medium-emphasis">(or First Name / Last Name)</span>
          </>,
          <>
            Player ID <span className="text-medium-emphasis">(optional, matched first)</span>
          </>,
          <>
            One column per stat{' '}
            <span className="text-medium-emphasis">
              (optional &mdash; RS, 4S, WKTS, CS&hellip;, saved to the scorecard)
            </span>
          </>,
        ]}
        info={
          <>
            Delete the rows for players you are not picking, then upload what is left. A match
            export works here unchanged: its figures are saved to this fixture&rsquo;s scorecard in
            the same pass, and any column that is not a player or a stat is ignored. Players already
            in the squad stay as they are &mdash; this only adds.
          </>
        }
        fileName={fileName}
        fileDetail={`${fileRowCount} row${fileRowCount === 1 ? '' : 's'}${
          statColumns.length
            ? ` and ${statColumns.length} stat column${statColumns.length === 1 ? '' : 's'}`
            : ''
        } detected`}
        busy={busy || columnsLoading}
        busyLabel={
          columnsLoading
            ? 'Loading this game’s scorecard columns…'
            : 'Reading and validating your file…'
        }
        disabled={columnsLoading}
        onFile={handleFile}
        onRemove={resetAll}
      />

      {inningsOptions?.length > 1 && (
        <div className="d-flex align-items-center gap-2 mt-3">
          <CFormLabel htmlFor="import-innings" className="mb-0 small fw-semibold">
            Save scores to
          </CFormLabel>
          <CFormSelect
            id="import-innings"
            style={{ width: 180 }}
            value={innings}
            onChange={(e) => onInningsChange(Number(e.target.value))}
          >
            {inningsOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </CFormSelect>
        </div>
      )}

      <div className="d-flex justify-content-between mt-4">
        <CButton type="button" color="secondary" variant="outline" onClick={onCancel}>
          Cancel
        </CButton>
        <CButton type="button" color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <ImportFileBar
        fileName={fileName}
        detail={`${fileRowCount} row${fileRowCount === 1 ? '' : 's'}`}
        onReplace={resetAll}
      />

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Already In Squad" value={summary.alreadyIn} tone="info" icon={cilPeople} />
      </div>

      {hasStats && (
        <InfoBox>
          This file carries scores as well as names. Included rows are added to the squad and their
          figures saved to the scorecard for {inningsLabel} — column{' '}
          <strong>{statColumns.map((c) => c.label).join(', ')}</strong> read from the file, blank
          cells left alone.
        </InfoBox>
      )}

      {ignoredHeaders.length > 0 && (
        <InfoBox color="light">
          Ignored column{ignoredHeaders.length === 1 ? '' : 's'}:{' '}
          <strong>{ignoredHeaders.join(', ')}</strong>. Only the player is read from this file.
        </InfoBox>
      )}

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>Name In File</CTableHeaderCell>
                <CTableHeaderCell style={{ minWidth: 280 }}>Player</CTableHeaderCell>
                {hasStats && <CTableHeaderCell>Scores</CTableHeaderCell>}
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const matchMeta = MATCH_META[r.match]
                return (
                  <CTableRow
                    key={r.row}
                    style={r.result === 'valid' ? undefined : tintStyle(meta.accent, '0d')}
                  >
                    <CTableDataCell className="ps-4 text-medium-emphasis">{r.row}</CTableDataCell>

                    <CTableDataCell>
                      <div className="fw-semibold">{r.csv_name || '—'}</div>
                      {r.csv_player_id && <code className="small">{r.csv_player_id}</code>}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={r.player_key}
                        onChange={(e) => remapRow(r.row, e.target.value)}
                      >
                        {/* Clearing a row that was pointed somewhere by hand puts it back on
                            whatever the file matched by itself, rather than emptying it. */}
                        <option value="">
                          {r.manual_id ? 'Undo manual mapping' : 'Select player'}
                        </option>
                        {playerOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </CFormSelect>
                      {matchMeta && (
                        <CBadge
                          color={matchMeta.color}
                          shape="rounded-pill"
                          className="px-2 py-1 mt-1"
                        >
                          {matchMeta.label}
                        </CBadge>
                      )}
                      {r.result === 'ambiguous_player' && (
                        <div className="small text-medium-emphasis mt-1">
                          {r.ambiguous_count} players share this name — pick the right one.
                        </div>
                      )}
                    </CTableDataCell>

                    {hasStats && (
                      <CTableDataCell>
                        <RowScores statColumns={statColumns} values={r.values} />
                        {r.invalid_cells > 0 && (
                          <div className="small text-warning-emphasis">
                            {r.invalid_cells} cell{r.invalid_cells === 1 ? '' : 's'} could not be
                            read and {r.invalid_cells === 1 ? 'was' : 'were'} skipped.
                          </div>
                        )}
                      </CTableDataCell>
                    )}

                    <CTableDataCell>
                      <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1">
                        {meta.label}
                      </CBadge>
                      {r.in_squad && r.result === 'valid' && (
                        <div>
                          <CBadge
                            color="info"
                            shape="rounded-pill"
                            className="px-2 py-1 mt-1"
                            title="Already in the squad — only their scores are imported"
                          >
                            Scores only
                          </CBadge>
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {r.result === 'valid' && (
                        <CButton
                          type="button"
                          size="sm"
                          color={r.included ? 'danger' : 'success'}
                          variant="outline"
                          title={r.included ? 'Exclude from import' : 'Include in import'}
                          onClick={() => toggleRowIncluded(r.row)}
                        >
                          {r.included ? 'Exclude' : 'Include'}
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {summary.needsAttention > 0 && (
        <InfoBox color="light">
          Rows that need attention are left out of the import. Point one at the right player to fix
          it, or leave it — the rest will still import.
        </InfoBox>
      )}

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CButton
          type="button"
          color="primary"
          disabled={!readyRows.length}
          onClick={() => goToStep(3)}
        >
          Continue
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
        <StatTile
          label="Ready to Add"
          value={readyRows.length}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Excluded"
          value={rows.length - readyRows.length}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Squad After Import"
          value={squadIds.size + newPlayerRows.length}
          tone="info"
          icon={cilPeople}
        />
      </div>

      {hasStats && (
        <InfoBox>
          {newPlayerRows.length} player{newPlayerRows.length === 1 ? '' : 's'} added to the squad,
          and {scoreRows.length} set{scoreRows.length === 1 ? '' : 's'} of figures saved to the
          scorecard for {inningsLabel}. Saving recalculates fantasy points.
          {skippedCells > 0 && (
            <>
              {' '}
              {skippedCells} cell{skippedCells === 1 ? '' : 's'} in the file could not be read and{' '}
              {skippedCells === 1 ? 'is' : 'are'} left out.
            </>
          )}
        </InfoBox>
      )}

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Player</CTableHeaderCell>
                <CTableHeaderCell>Position</CTableHeaderCell>
                <CTableHeaderCell>Team</CTableHeaderCell>
                {hasStats && <CTableHeaderCell>Scores</CTableHeaderCell>}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {readyRows.map((r) => (
                <CTableRow key={r.row}>
                  <CTableDataCell className="ps-4">
                    <div className="fw-semibold">{r.player_name}</div>
                    {r.in_squad && (
                      <div className="small text-medium-emphasis">
                        Already in squad — scores only
                      </div>
                    )}
                  </CTableDataCell>
                  <CTableDataCell>{POSITION_META[r.player_position]?.label || '—'}</CTableDataCell>
                  <CTableDataCell>{r.team_name || '—'}</CTableDataCell>
                  {hasStats && (
                    <CTableDataCell>
                      <RowScores statColumns={statColumns} values={r.values} />
                    </CTableDataCell>
                  )}
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(2)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CLoadingButton
          type="button"
          color="success"
          loading={importing}
          disabled={!readyRows.length}
          onClick={runImport}
        >
          <CIcon icon={cilCloudUpload} className="me-1" />
          {hasStats
            ? `Import ${readyRows.length} Player${readyRows.length === 1 ? '' : 's'} & Scores`
            : `Add ${readyRows.length} Player${readyRows.length === 1 ? '' : 's'}`}
        </CLoadingButton>
      </div>
    </>
  )

  if (!playerPool.length) {
    return (
      <InfoBox color="light">
        There are no players to pick from yet. Add players to the club before importing a squad.
      </InfoBox>
    )
  }

  return (
    <>
      <MiniStepper
        steps={WIZARD_STEPS}
        current={wizardStep}
        maxReached={maxWizardStep}
        onStepClick={goToStep}
      />
      <ImportErrorAlert errors={importErrors} />
      {wizardStep === 1 && renderUpload()}
      {wizardStep === 2 && renderValidate()}
      {wizardStep === 3 && renderReview()}
    </>
  )
}

export default SquadCsvImport
